import React from "react";
import "./App.css";
import Navbar from "./components/Navbar";
import Section from "./components/Section";
import Footer from "./components/Footer";

const researchHighlights = [
  {
    label: "Phoridae",
    text: "Taxonomy, morphology, evolution, and biodiversity informatics for small flies.",
  },
  {
    label: "Millipede-killing flies",
    text: "Species descriptions, parasitism, phylogeny, and natural history.",
  },
  {
    label: "Fieldwork",
    text: "Research shaped by collecting in some of the most biodiverse places on the planet.",
  },
];

const softwareFocus = [
  "Web and cloud technologies",
  "API design and testing",
  "Geospatial and biodiversity data",
  "Interfaces for research workflows",
];

const projects = [
  {
    title: "Multi-entry species keys",
    text: "Taxon-centered pages, identification keys, and structured notes for fly diversity.",
  },
  {
    title: "Collection app",
    text: "Field and specimen data tools aimed at making natural-history records easier to capture.",
  },
  {
    title: "Phorid.net",
    text: "The one-stop shop for learning about phorid flies and engaging in biodiversity data.",
  }
];

const cvUrl = `${process.env.PUBLIC_URL || ""}/downloads/Hash_CV_2026_FEB_28.pdf`;

export default function App() {
  return (
    <div className="App">
      <Navbar />

      <main>
        <Section id="about" className="hero">
          <div className="heroLayout">
            <div>
              <p className="kicker">Software / Entomology / Biodiversity</p>
              <h1>John Hash</h1>
              <p className="tagline">Always exploring. Always learning.</p>
              <p className="lede">
                Software engineer and entomologist focused on
                Phoridae systematics, fieldwork, biodiversity research and data-driven tools.
              </p>

              <div className="heroActions">
                <a className="btn primary" href="#cv">View CV</a>
                <a className="btn" href="#projects">Projects</a>
                <a className="btn" href="#contact">Contact</a>
              </div>
            </div>

            <aside className="heroNotes" aria-label="Profile highlights">
              <p className="eyebrow">Current threads</p>
              <ul>
                <li>Natural history and systematics of phorid flies</li>
                <li>Research software for data collection and exploration</li>
                <li>Usable tools for people doing careful scientific work</li>
              </ul>
            </aside>
          </div>
        </Section>

        <Section id="research" title="Research">
          <div className="splitSection">
            <div>
              <p className="definition">
                <strong>Entomology</strong> is the scientific study of insects.
              </p>
              <p>
                I have been studying the taxonomy and evolution of small flies in
                the family Phoridae for over a decade. Along the way, I have
                traveled extensively and performed fieldwork in some of the most
                biodiverse places on the planet.
              </p>
              <p>
                Most of my research focuses on describing new species of flies that
                parasitize millipedes, with related work in zooarchaeology,
                taphonomy, biodiversity, evolution, and ecology.
              </p>
            </div>

            <div className="detailStack">
              {researchHighlights.map((item) => (
                <div className="detailItem" key={item.label}>
                  <span>{item.label}</span>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section id="code" title="Software">
          <div className="splitSection codeSection">
            <div>
              <p>
                My interest in programming started in high school making games in
                QBASIC and Visual Basic with friends. I drifted toward the
                biological sciences and entomology, then found my way back to
                programming in graduate school.
              </p>
              <p>
                I am especially interested in web and cloud technologies, API
                design and testing, and interfaces that make complex research
                tasks feel more usable and enjoyable.
              </p>
            </div>

            <div className="pillGrid" aria-label="Software focus areas">
              {softwareFocus.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        </Section>

        <Section id="projects" title="Selected Tools and Projects">
          <div className="projectGrid">
            {projects.map((project) => (
              <article className="card" key={project.title}>
                <h3>{project.title}</h3>
                <p>{project.text}</p>
              </article>
            ))}
          </div>
        </Section>

        <Section id="cv" title="CV">
          <p>
            Download:{" "}
            <a href={cvUrl} target="_blank" rel="noreferrer">
              Hash_CV_2026_FEB_28.pdf
            </a>
          </p>
          <p className="muted">
            The full CV includes education, appointments, skills, publications,
            and research experience.
          </p>
        </Section>

        <Section id="contact" title="Contact">
          <ul className="list">
            <li>
              GitHub:{" "}
              <a href="https://github.com/phoridae" target="_blank" rel="noreferrer">
                phoridae
              </a>
            </li>
            <li>
              GitHub:{" "}
              <a href="https://github.com/phoridFly?tab=repositories" target="_blank" rel="noreferrer">
                phoridFly (legacy repo)
              </a>
            </li>
            <li>
              Google Scholar:{" "}
              <a href="https://scholar.google.com/citations?user=L3eJEPwAAAAJ&hl=en" target="_blank" rel="noreferrer">
                John M Hash
              </a>
            </li>
            <li>
              Instagram:{" "}
              <a href="https://www.instagram.com/phorid1/" target="_blank" rel="noreferrer">
                @phorid1
              </a>
            </li>
          </ul>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
